import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddMenu from "../modal/AddMenuModal";
import { useDeleteMenu, useGetAllMenu, useGetAllCategory } from "../hooks/hooks";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import DeleteModal from "../modal/DeleteModal";
import { showSuccessToast, showErrorToast } from './../components/common'
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import MenuDetailsModal from "../modal/MenuDetailsModal";
import Header from "../components/Common/Header";
import { DeleteIcon, DropDownArrowIcon, EditIcon, NDMenuI, ViewIcon } from "../assets/icons/icon";
import NoDataFound from "../components/Common/NoDataFound";
import SearchControls from "../components/Common/SearchControls";
import Pagination from "../components/Common/Pagination";
import { usePagination } from "../hooks/usePagination";
import { ButtonWithIcon } from "../components/Common/Buttons";

const Menu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: menuLists, error: error, isLoading: IsLoading, refetch } = useGetAllMenu();
  const { data: fetchedCategories } = useGetAllCategory();
  const { mutate: deleteMenu } = useDeleteMenu();

  const [isOpenDelete, setisOpenDelete] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isEditItem, setIsEditItem] = useState(undefined);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isDeleteCategory, setIsDeleteCategory] = useState(undefined);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(undefined);

  const [allMenuList, setAllMenuList] = useState(menuLists);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categorySearch, setCategorySearch] = useState('');
  const [filteredMenuList, setFilteredMenuList] = useState(menuLists);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const allMenuRef = useRef(menuLists);
  const categoryDropdownRef = useRef(null);

  const applyFilters = useCallback((searchValue, categoryValue) => {
    if (!allMenuRef.current || !Array.isArray(allMenuRef.current)) {
      console.warn("allMenuRef is not ready or not an array");
      return;
    }

    const normalizedSearch = (searchValue || '').trim().toLowerCase();
    const normalizedCategory = categoryValue || 'all';

    const filtered = allMenuRef.current.filter((menu) => {
      const guj = menu.menuName?.toLowerCase() || "";
      const eng = menu.menuNameEn?.toLowerCase() || "";
      const matchesSearch = !normalizedSearch || guj.includes(normalizedSearch) || eng.includes(normalizedSearch);
      const menuCategoryId = menu?.category?._id;
      const matchesCategory = normalizedCategory === 'all' || menuCategoryId === normalizedCategory;

      return matchesSearch && matchesCategory;
    });

    setFilteredMenuList(filtered);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (menuLists) {
      setAllMenuList(menuLists);
      allMenuRef.current = menuLists;
      applyFilters(searchText, selectedCategory);
    }
  }, [menuLists, applyFilters, searchText, selectedCategory]);

  const categoryOptions = useMemo(() => {
    if (Array.isArray(fetchedCategories) && fetchedCategories.length > 0) {
      return fetchedCategories;
    }

    if (!allMenuList || !Array.isArray(allMenuList)) return [];

    const uniqueCategories = new Map();
    allMenuList.forEach((menu) => {
      const category = menu?.category;
      if (category?._id && !uniqueCategories.has(category._id)) {
        uniqueCategories.set(category._id, category);
      }
    });

    return Array.from(uniqueCategories.values());
  }, [fetchedCategories, allMenuList]);

  const filteredCategoryOptions = useMemo(() => {
    if (!categorySearch.trim()) return categoryOptions;
    const value = categorySearch.trim().toLowerCase();
    return categoryOptions.filter((cat) => {
      const name = cat?.categoryName?.toLowerCase() || '';
      const nameEn = cat?.categoryNameEn?.toLowerCase() || '';
      return name.includes(value) || nameEn.includes(value);
    });
  }, [categoryOptions, categorySearch]);

  const handleSearchChange = useCallback((value) => {
    applyFilters(value, selectedCategory);
  }, [applyFilters, selectedCategory]);

  const debouncedHandleSearch = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [debouncedHandleSearch]);

  const handleCategoryChange = useCallback((value) => {
    setSelectedCategory(value);
    applyFilters(searchText, value);
  }, [applyFilters, searchText]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isCategoryDropdownOpen) {
      setCategorySearch('');
    }
  }, [isCategoryDropdownOpen]);

  const handleOpenPopup = () => {
    navigate('/addmenu');
  };

  const handleClosePopup = async (isRefresh) => {
    setPopupOpen(false);
    setIsEdit(false);
    setIsEditItem(undefined);

    if (isRefresh === true) {
      refetch();
    }
  };

  const onDelete = (isDelete) => {
    if (isDelete) {
      handleDeleteCategory(isDeleteCategory);
    }
    setisOpenDelete(false)
  }

  const handleDeleteCategory = (item) => {
    deleteMenu(item, {
      onSuccess: async (res) => {
        refetch();
        showSuccessToast(res?.data?.message);
      },
      onError: (err) => {
        showErrorToast(err.response.data.error)
      },
    });
  };

  // Pagination calculations
  const totalItems = filteredMenuList?.length || 0;

  const {
    totalPages,
    startIndex,
    endIndex,
    pageNumbers,
    onPageChange,
    onItemsPerPageChange
  } = usePagination({
    totalItems,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage
  });

  const currentItems = filteredMenuList?.slice(startIndex, endIndex) || [];

  const MenuCard = ({ item, index }) => (
    <div className="bg-white rounded-xl shadow-card border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
      {/* Image Placeholder */}
      <div className="h-32 bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
        <svg className="w-12 h-12 text-primary/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {item.menuName}
            </h3>
            {item.menuNameEn && (
              <p className="text-sm text-gray-500">{item.menuNameEn}</p>
            )}
          </div>
          {item?.price && (
            <div className="text-xl font-bold text-primary">
              ₹{item.price}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <span className="text-sm text-gray-600">
            {item?.category?.categoryName || 'N/A'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ButtonWithIcon title="View" variant='secondary' Icon={ViewIcon} btnclass="p-2 text-blue-600 hover:bg-blue-50 rounded-md border border-blue-300" onClick={(e) => {
              e.stopPropagation();
              setIsViewOpen(true);
              setViewItem(item);
            }} />
            <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} btnclass="p-2 text-green-600 hover:bg-green-50 rounded-md transition-all border border-green-300" iconClass="mr-0" onClick={(e) => {
              e.stopPropagation();
              setIsEdit(true);
              setIsEditItem(item);
              navigate('/addmenu', { state: { selectedMenu: item, isEdit: true } });
            }} />
            <ButtonWithIcon title="Delete" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0" btnclass="p-2 text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={(e) => {
              e.stopPropagation();
              setIsDeleteCategory(item);
              setisOpenDelete(true);
            }} />
          </div>
        </div>
      </div>
    </div>
  );

  const TableRow = ({ item, index }) => (
    <div
      className={`w-full flex py-4 font-medium text-sm text-gray-700 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200
                ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"}`}
    >
      <div className="w-[10%] pl-6 flex items-center font-medium text-gray-500">{startIndex + index + 1}</div>
      <div className="w-[35%] flex items-center">
        <div>
          <div className="font-semibold text-gray-900">{item.menuNameEn}</div>
          {item.menuName && (
            <div className="text-xs text-gray-500">{item.menuName}</div>
          )}
        </div>
      </div>
      <div className="w-[30%] flex items-center">
        <div>
          {item?.category?.categoryNameEn || item?.category?.categoryName ? (
            <>
              <div className="text-gray-700">
                {item?.category?.categoryNameEn}
              </div>

              {item?.category?.categoryName && (
                <div className="text-xs text-gray-500">
                  {item?.category?.categoryName}
                </div>
              )}
            </>
          ) : (
            <div className="text-gray-700">N/A</div>
          )}
        </div>
      </div>

      <div className="w-[15%] flex items-center">
        {item?.price ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
            ₹{item.price}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </div>
      <div className="w-[10%] flex items-center">
        <div className="flex items-center gap-2">
          <ButtonWithIcon title="View" variant='secondary' Icon={ViewIcon} btnclass="p-2 text-blue-600 hover:bg-blue-50 rounded-md border border-blue-300" onClick={(e) => {
            e.stopPropagation();
            setIsViewOpen(true);
            setViewItem(item);
          }} />
          <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} btnclass="p-2 text-green-600 hover:bg-green-50 rounded-md transition-all border border-green-300" iconClass="mr-0" onClick={(e) => {
            e.stopPropagation();
            setIsEdit(true);
            setIsEditItem(item);
            navigate('/addmenu', { state: { selectedMenu: item, isEdit: true } });
          }} />
          <ButtonWithIcon title="Delete" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0" btnclass="p-2 text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={(e) => {
            e.stopPropagation();
            setIsDeleteCategory(item);
            setisOpenDelete(true);
          }} />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-6 py-6">
          {/* Header Section */}
          <Header title={t('menu')} subTitle={t('manage_your_menu_items')} buttonText={t('btn_add_new_menu')} handleClick={handleOpenPopup} />

          {/* Search and Controls Section */}
          <SearchControls
            searchText={searchText}
            onSearchChange={setSearchText}
            onSearchDebounced={debouncedHandleSearch}
            placeholder={t('search_Menu_lbl')}
            viewMode={viewMode}
            onViewChange={setViewMode}
            totalItems={totalItems}
          >
            <div className="w-full md:w-64 relative" ref={categoryDropdownRef}>
              <button
                type="button"
                onClick={() => setIsCategoryDropdownOpen((prev) => !prev)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-left text-gray-900 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 flex items-center justify-between"
              >
                <span className="truncate">
                  {selectedCategory === 'all'
                    ? 'All categories'
                    : categoryOptions.find((c) => c._id === selectedCategory)?.categoryNameEn ||
                    categoryOptions.find((c) => c._id === selectedCategory)?.categoryName ||
                    'Select category'}
                </span>
                <DropDownArrowIcon className={`w-4 h-4 ${isCategoryDropdownOpen ? '-rotate-90' : 'rotate-90'}`} />
              </button>

              {isCategoryDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      placeholder="Search category"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder-gray-500"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <button
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-primary/10 ${selectedCategory === 'all' ? 'bg-primary/5 font-semibold' : 'text-gray-700'
                        }`}
                      onClick={() => {
                        handleCategoryChange('all');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      All categories
                    </button>
                    {filteredCategoryOptions.length > 0 ? (
                      filteredCategoryOptions.map((category) => (
                        <button
                          key={category._id}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-primary/10 ${selectedCategory === category._id ? 'bg-primary/5 font-semibold' : 'text-gray-700'
                            }`}
                          onClick={() => {
                            handleCategoryChange(category._id);
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          {category.categoryNameEn
                            ? `${category.categoryNameEn}${category.categoryName ? ` (${category.categoryName})` : ''}`
                            : category.categoryName || 'N/A'}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-500">No categories found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SearchControls>


          {/* Content Section */}
          <div className="bg-white rounded-xl shadow-card overflow-hidden">
            {IsLoading ? (
              <>
                {viewMode === 'table' ? (
                  <>
                    {/* Table Header Skeleton */}
                    <div className="bg-gray-50 border-b border-gray-200">
                      <div className="flex py-4 px-6 text-sm font-semibold text-gray-700">
                        <div className="w-[10%]">#</div>
                        <div className="w-[35%]">{t('menu_name')}</div>
                        <div className="w-[30%]">{t('Category')}</div>
                        <div className="w-[15%]">{t('price')}</div>
                        <div className="w-[10%]">{t('Action')}</div>
                      </div>
                    </div>

                    {/* Table Body Skeleton */}
                    <div>
                      {Array.from({ length: 6 }, (_, index) => (
                        <div
                          key={index}
                          className={`w-full flex py-4 border-b border-gray-100 ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"
                            }`}
                        >
                          <div className="w-[10%] pl-6 flex items-center">
                            <Skeleton height={16} width={20} />
                          </div>
                          <div className="w-[35%] flex items-center pr-4">
                            <div className="flex-1">
                              <Skeleton height={18} width="80%" />
                              <Skeleton height={14} width="60%" className="mt-1" />
                            </div>
                          </div>
                          <div className="w-[30%] flex items-center pr-4">
                            <div className="flex-1">
                              <Skeleton height={18} width="80%" />
                              <Skeleton height={14} width="60%" className="mt-1" />
                            </div>
                          </div>

                          <div className="w-[15%] flex items-center pr-4">
                            <div className="flex-1 items-center gap-2">
                              <Skeleton height={18} width="80%" />
                              <Skeleton height={14} width="60%" className="mt-1" />
                            </div>
                          </div>
                          <div className="w-[10%] flex items-center">
                            <div className="flex items-center gap-2">
                              <Skeleton height={32} width={32} className="rounded-lg" />
                              <Skeleton height={32} width={32} className="rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  /* Grid View Skeleton */
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {Array.from({ length: 8 }, (_, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-card border border-gray-100 p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <Skeleton height={20} width="70%" className="mb-2" />
                              <div className="flex items-center gap-2">
                                <Skeleton height={16} width={16} className="rounded" />
                                <Skeleton height={14} width="50%" />
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Skeleton height={32} width={32} className="rounded-lg" />
                              <Skeleton height={32} width={32} className="rounded-lg" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pagination Skeleton */}
                <div className="flex items-center justify-between gap-4 px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Skeleton height={16} width={40} />
                      <Skeleton height={32} width={60} className="rounded-md" />
                      <Skeleton height={16} width={50} />
                    </div>
                    <Skeleton height={16} width={120} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton height={32} width={70} className="rounded-md" />
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }, (_, index) => (
                        <Skeleton key={index} height={32} width={40} className="rounded-md" />
                      ))}
                    </div>
                    <Skeleton height={32} width={50} className="rounded-md" />
                  </div>
                </div>
              </>
            ) : (
              <>
                {viewMode === 'table' ? (
                  <>
                    {/* Table Header */}
                    <div className="bg-gray-50 border-b border-gray-200">
                      <div className="flex py-4 text-sm font-semibold text-gray-700">
                        <div className="w-[10%] pl-6">#</div>
                        <div className="w-[35%]">{t('menu_name')}</div>
                        <div className="w-[30%]">{t('Category')}</div>
                        <div className="w-[15%]">{t('price')}</div>
                        <div className="w-[10%]">{t('Action')}</div>
                      </div>
                    </div>

                    {/* Table Body */}
                    <div>
                      {currentItems && currentItems.length > 0 ? (
                        currentItems.map((item, index) => (
                          <TableRow key={index} item={item} index={index} />
                        ))
                      ) : (
                        <NoDataFound text='No menu items found' desc={searchText ? 'Try adjusting your search terms' : 'Get started by adding your first menu item'} icon={<NDMenuI />} showButton={!searchText} buttonText='Add Menu Item' handleClick={handleOpenPopup} />
                      )}
                    </div>
                  </>
                ) : (

                  <div className="p-6">
                    {currentItems && currentItems.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {currentItems.map((item, index) => (
                          <MenuCard key={index} item={item} index={index} />
                        ))}
                      </div>
                    ) : (
                      <NoDataFound text='No menu items found' desc={searchText ? 'Try adjusting your search terms' : 'Get started by adding your first menu item'} icon={<NDMenuI />} showButton={!searchText} buttonText='Add Menu Item' handleClick={handleOpenPopup} />
                    )}
                  </div>
                )}

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageNumbers={pageNumbers}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  onPageChange={onPageChange}
                  onItemsPerPageChange={onItemsPerPageChange}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <DeleteModal isOpen={isOpenDelete} onClose={onDelete} confirmMsg={t('del_confirm')} />

      {isPopupOpen && (
        <AddMenu
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          isEdit={isEdit}
          isEditItem={isEditItem}
        />
      )}

      <MenuDetailsModal
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false);
          setViewItem(undefined);
        }}
        item={viewItem}
      />
    </>
  );
};

export default Menu;


{/* <div className="bg-white rounded-xl shadow-card p-6 mb-6">
            <div className="flex flex-row items-center justify-between gap-4 flex-wrap md:flex-nowrap">
              <div className="flex items-center gap-4 flex-1">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <img className="h-5 w-5 text-gray-400" src={require('./../assets/images/search.svg').default} alt="search" />
                    </div>
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        debouncedHandleSearch(e.target.value);
                      }}
                      placeholder={t('search_Menu_lbl')}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 text-gray-900 placeholder-gray-500"
                    />
                  </div>
                </div>

                <div className="w-full md:w-64 relative" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsCategoryDropdownOpen((prev) => !prev)}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-left text-gray-900 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 flex items-center justify-between"
                  >
                    <span className="truncate">
                      {selectedCategory === 'all'
                        ? 'All categories'
                        : categoryOptions.find((c) => c._id === selectedCategory)?.categoryNameEn ||
                        categoryOptions.find((c) => c._id === selectedCategory)?.categoryName ||
                        'Select category'}
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isCategoryDropdownOpen ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isCategoryDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                      <div className="p-2 border-b border-gray-200 bg-gray-50">
                        <input
                          type="text"
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          placeholder="Search category"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder-gray-500"
                        />
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        <button
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-primary/10 ${selectedCategory === 'all' ? 'bg-primary/5 font-semibold' : 'text-gray-700'
                            }`}
                          onClick={() => {
                            handleCategoryChange('all');
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          All categories
                        </button>
                        {filteredCategoryOptions.length > 0 ? (
                          filteredCategoryOptions.map((category) => (
                            <button
                              key={category._id}
                              className={`w-full text-left px-4 py-2 text-sm hover:bg-primary/10 ${selectedCategory === category._id ? 'bg-primary/5 font-semibold' : 'text-gray-700'
                                }`}
                              onClick={() => {
                                handleCategoryChange(category._id);
                                setIsCategoryDropdownOpen(false);
                              }}
                            >
                              {category.categoryNameEn
                                ? `${category.categoryNameEn}${category.categoryName ? ` (${category.categoryName})` : ''}`
                                : category.categoryName || 'N/A'}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500">No categories found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'table'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${viewMode === 'grid'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                </div>

                <div className="text-sm text-gray-500">
                  {totalItems} {t('items')}
                </div>
              </div>
            </div>
          </div> */}